import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Minus, Plus, Loader2, Play, Package } from 'lucide-react';
import { UserData } from '../hooks/useUser';
import CurrencyIcon from './CurrencyIcon';
import ChestIcon from './ChestIcon';
import { cn } from '../lib/utils';
import { audioEngine } from '../utils/audioEngine';


interface QuantityPickerModalProps {
  user: UserData;
  tier: 'Bronze' | 'Silver' | 'Gold' | 'Diamond' | 'Legendary';
  onConfirm: (paymentMethod: 'GC' | 'CC' | 'Ad' | 'Inventory', count: number) => Promise<void>;
  onClose: () => void;
  isOpening: boolean;
  purchaseOnly?: boolean;
}

const BOX_PRICES = {
  Bronze: { Ad: 0 },
  Silver: { GC: 1000 },
  Gold: { CC: 20 },
  Diamond: { CC: 50 }
};

export default function QuantityPickerModal({ user, tier, onConfirm, onClose, isOpening, purchaseOnly }: QuantityPickerModalProps) {
  const [paymentMethod, setPaymentMethod] = useState<'GC' | 'CC' | 'Ad' | 'Inventory'>(purchaseOnly ? 'GC' : 'Inventory');
  const owned = user.rewardBoxes[tier.toLowerCase() as keyof UserData['rewardBoxes']] || 0;
  const config = BOX_PRICES[tier];

  // Auto-select best payment method if Inventory is empty
  React.useEffect(() => {
    if (owned > 0 && !purchaseOnly) {
      setPaymentMethod('Inventory');
    } else if ('GC' in config && user.coins >= config.GC!) {
      setPaymentMethod('GC');
    } else if ('CC' in config && user.gems >= config.CC!) {
      setPaymentMethod('CC');
    } else if ('Ad' in config) {
      setPaymentMethod('Ad');
    }
  }, [tier, owned, purchaseOnly]);

  const getMaxQuantity = () => {
    if (paymentMethod === 'Inventory') return Math.min(owned, 10);
    if (paymentMethod === 'Ad') return 1;
    
    let balance = 0;
    let price = 0;
    
    if (paymentMethod === 'GC') {
      balance = user.coins || 0;
      price = (config as any).GC || 0;
    } else if (paymentMethod === 'CC') {
      balance = user.gems || 0;
      price = (config as any).CC || 0;
    }
    
    if (price <= 0) return 1;
    return Math.max(0, Math.min(Math.floor(balance / (price || 1)), 10));
  };

  const max = Math.max(0, getMaxQuantity() || 0);
  const [count, setCount] = useState(isNaN(max) ? 0 : Math.max(0, Math.min(1, max)));
  
  // Sync count if max changes (e.g. user changes payment method)
  React.useEffect(() => {
    const safeMax = isNaN(max) ? 0 : max;
    if (count > safeMax) {
      setCount(Math.max(0, safeMax));
    } else if (count === 0 && safeMax > 0) {
      setCount(1);
    }
  }, [max]);

  const currentPrice = paymentMethod === 'Inventory' || paymentMethod === 'Ad' 
    ? 0 
    : ((config as any)[paymentMethod] || 0) * count;

  return (
    <div className="absolute inset-0 z-[1000] flex items-center justify-center p-6">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-[#f0f9ff]/80 backdrop-blur-sm" 
        onClick={onClose} 
      />
      
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 30 }}
        className="relative game-card-3d border-none bg-white p-8 w-full max-w-sm shadow-2xl"
      >
        <button 
          onClick={() => {
            audioEngine.playSound('click');
            onClose();
          }}
          className="absolute top-4 right-4 p-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-400 transition-all active:scale-90"
        >
          <X className="w-5 h-5" />
        </button>


        <div className="flex flex-col items-center mb-8">
          <div className={cn(
            "w-20 h-20 rounded-3xl flex items-center justify-center mb-4 relative overflow-hidden boarder-2 transition-all",
            tier === 'Bronze' ? "bg-amber-50 border-amber-100" :
            tier === 'Silver' ? "bg-slate-50 border-slate-200" :
            tier === 'Gold' ? "bg-yellow-50 border-yellow-100" :
            "bg-cyan-50 border-cyan-100"
          )}>
             <ChestIcon tier={tier.toLowerCase() as any} size="lg" />
          </div>
          <h3 className="text-3xl font-num text-slate-900 uppercase tracking-tight text-center">{tier} Cache</h3>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Select amount to extract</p>
        </div>

        {/* Payment Selection - Only show if not in purchase-only mode */}
        {!purchaseOnly && (
          <div className="flex flex-col gap-2 mb-8">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">SELECT METHOD</p>
            <div className="grid grid-cols-2 gap-3">
              {owned > 0 && (
                <button
                  onClick={() => {
                    audioEngine.playSound('click');
                    setPaymentMethod('Inventory'); 
                  }}
                  className={`py-3 rounded-2xl font-black uppercase text-[10px] flex flex-col items-center justify-center transition-all toy-3d-button border-none ${paymentMethod === 'Inventory' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400 opacity-60'}`}
                >
                  <span>INVENTORY</span>
                  <span className="text-[8px] opacity-40">({owned} OWNED)</span>
                </button>

              )}
              {'GC' in config && (
                <button
                  onClick={() => {
                    audioEngine.playSound('click');
                    setPaymentMethod('GC'); 
                  }}
                  className={`py-3 rounded-2xl font-black uppercase text-[10px] flex items-center justify-center gap-2 transition-all toy-3d-button border-none ${paymentMethod === 'GC' ? 'bg-yellow-400 text-slate-900' : 'bg-slate-100 text-slate-400 opacity-60'}`}
                >
                  <CurrencyIcon type="coin" size="sm" /> {(config as any).GC}
                </button>

              )}
              {'CC' in config && (
                <button
                  onClick={() => {
                    audioEngine.playSound('click');
                    setPaymentMethod('CC'); 
                  }}
                  className={`py-3 rounded-2xl font-black uppercase text-[10px] flex items-center justify-center gap-2 transition-all toy-3d-button border-none ${paymentMethod === 'CC' ? 'bg-purple-500 text-white' : 'bg-slate-100 text-slate-400 opacity-60'}`}
                >
                  <CurrencyIcon type="gem" size="sm" /> {(config as any).CC}
                </button>

              )}
              {'Ad' in config && (
                <button
                  onClick={() => {
                    audioEngine.playSound('click');
                    setPaymentMethod('Ad'); 
                  }}
                  className={`py-3 rounded-2xl font-black uppercase text-[10px] flex items-center justify-center gap-2 transition-all toy-3d-button border-none ${paymentMethod === 'Ad' ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-400 opacity-60'}`}
                >
                  {user.isPremium ? (
                    'CLAIM'
                  ) : (
                    <>
                      <Play className="w-3 h-3 fill-current" /> WATCH AD
                    </>
                  )}
                </button>

              )}
            </div>
          </div>
        )}

        {/* Quantity Selector */}
        {paymentMethod !== 'Ad' && (
          <div className="flex flex-col gap-2 mb-8">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">SET QUANTITY</p>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => {
                  audioEngine.playSound('click');
                  setCount(prev => Math.max(1, prev - 1));
                }}
                disabled={count <= 1}
                className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center transition-all toy-3d-button border-none disabled:opacity-30 active:translate-y-1"
              >
                <Minus className="w-6 h-6 text-slate-900" />
              </button>

              
              <div className="flex-1 h-12 bg-slate-900 rounded-2xl text-white flex items-center justify-center font-num text-3xl">
                {count}
              </div>

              <button 
                onClick={() => {
                  audioEngine.playSound('click');
                  setCount(prev => Math.min(max, prev + 1));
                }}
                disabled={count >= max}
                className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center transition-all toy-3d-button border-none disabled:opacity-30 active:translate-y-1"
              >
                <Plus className="w-6 h-6 text-slate-900" />
              </button>

            </div>
            {max > 1 && (
              <div className="flex gap-2 mt-3">
                {[5, 10].map(v => (
                  <button
                    key={v}
                    onClick={() => {
                      audioEngine.playSound('click');
                      setCount(Math.min(v, max));
                    }}
                    className="flex-1 py-1.5 bg-slate-50 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-colors border-none"
                  >
                    Set {Math.min(v, max)}x
                  </button>

                ))}
              </div>
            )}
          </div>
        )}

        <button
          onClick={() => {
            audioEngine.playSound('click');
            onConfirm(paymentMethod, count);
          }}

          disabled={isOpening || (count === 0) || (paymentMethod !== 'Inventory' && (currentPrice > (paymentMethod === 'GC' ? user.coins : user.gems)))}
          className="w-full bg-[#fbbf24] py-5 rounded-[28px] font-num text-2xl uppercase tracking-tighter toy-3d-button border-none text-slate-900 disabled:opacity-50 flex items-center justify-center gap-3 transition-all"
        >
          {isOpening ? (
            <Loader2 className="animate-spin w-6 h-6 text-slate-900" />
          ) : (
            <>
              {paymentMethod === 'Ad' ? (
                <div className="flex items-center gap-2">
                  {!user.isPremium && <Play className="w-6 h-6 fill-slate-900" />}
                  <span>{user.isPremium ? 'CLAIM' : 'WATCH AD'}</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                   {paymentMethod === 'Inventory' && <span>OPEN {count}x</span>}
                   {currentPrice > 0 && (
                     <div className="flex items-center gap-2.5 px-4 py-1.5 bg-black/10 rounded-2xl text-lg font-num font-black">
                        <CurrencyIcon type={paymentMethod === 'GC' ? 'coin' : 'gem'} size="sm" />
                        {currentPrice.toLocaleString()}
                     </div>
                   )}
                </div>
              )}
            </>
          )}
        </button>
      </motion.div>
    </div>
  );
}
